import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import GlobalThrobberComponent from '../components/global_throbber_component'
import CarouselComponent from '../components/carousel_component'
import { useNotification } from '../components/global_noification_component'
import '../css/items_page.css'

const API_BASE = process.env.REACT_APP_API_BASE
const API_VERSION = process.env.REACT_APP_API_ENVIRONMENT

export default function ItemsPage() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [quickFilter, setQuickFilter] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [advPurchaseType, setAdvPurchaseType] = useState('')
  const [advAccessType, setAdvAccessType] = useState('')
  const [expandedBundles, setExpandedBundles] = useState({})
  const [selectedBundle, setSelectedBundle] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(24)
  const [searchDebounce, setSearchDebounce] = useState('')
  const PURCHASE_OPTIONS = ['', 'Unlockable', 'Bundle', 'Coins', 'Purchase']
  const ACCESS_OPTIONS = ['', 'Public', 'role_base', 'pro_only']

  const fileInputRef = useRef(null)
  const [matchModalOpen, setMatchModalOpen] = useState(false)
  const [matchLoading, setMatchLoading] = useState(false)
  const [matchedItems, setMatchedItems] = useState([])

  const formatDate = useCallback((dateStr) => {
    const d = new Date(dateStr)
    if (Number.isNaN(d.getTime())) return dateStr
    return new Intl.DateTimeFormat(navigator.language, { dateStyle: 'medium' }).format(d)
  }, [])

  const prettify = useCallback(str => str ? str.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '', [])

  const QUICK_FILTER_MAP = useMemo(() => ({
    character_hair: ['character', 'hair'],
    character_facial_hair: ['character', 'facial hair'],
    clothes_base: ['clothes', 'irl'],
    clothes_jersey: ['clothes', 'jersey'],
    clothes_mascot: ['clothes', 'mascot'],
    clothes_catchers: ['clothes', 'catchers gear'],
    accessories_eyes: ['accessories', 'eyes'],
    accessories_head: ['accessories', 'head'],
    accessories_hands: ['accessories', 'hands'],
    accessories_tattoos: ['accessories', 'tattoos'],
    accessories_gadgets: ['accessories', 'gadgets'],
    tags_frames: ['tag/effects', 'frames'],
    tags_badges: ['tag/effects', 'badges'],
    tags_sounds: ['tag/effects', 'sounds']
  }), [])
  const QUICK_BUTTONS = [
    ['bundles_only','🎁'],
    ['character_hair','💇'],
    ['character_facial_hair','🧔'],
    ['clothes_base','👕'],
    ['clothes_jersey','⚾'],
    ['clothes_mascot','🐻'],
    ['clothes_catchers','🎯'],
    ['accessories_eyes','🕶️'],
    ['accessories_head','🎩'],
    ['accessories_hands','🧤'],
    ['accessories_tattoos','🔖'],
    ['accessories_gadgets','📱'],
    ['tags_frames','🖼️'],
    ['tags_badges','🏅'],
    ['tags_sounds','🎵']
  ]

  const notify = useNotification()

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(search)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchDebounce, quickFilter, advPurchaseType, advAccessType])

  useEffect(() => {
    let canceled = false
    const loadAll = async () => {
      setLoading(true)
      try {
        const collected = []
        let lastKey = ''
        const PAGE = 250
        do {
          const qs = new URLSearchParams({ limit: String(PAGE) })
          if (lastKey) qs.append('lastKey', lastKey)
          const res = await fetch(`${API_BASE}/${API_VERSION}/private/gym-class-item-category?` + qs.toString(), { method: 'GET' })
          if (!res.ok) throw new Error('Failed to load')
          const data = await res.json()
          collected.push(...(data.items || []))
          lastKey = data.lastKey || ''
        } while (lastKey && !canceled)
        if (!canceled) setItems(collected)
      } catch (err) {
        if (!canceled) notify('error', err.message)
      } finally {
        if (!canceled) setLoading(false)
      }
    }
    loadAll()
    return () => { canceled = true }
  }, [notify])

  const filtered = useMemo(() => {
    let result = items
    if (quickFilter === 'bundles_only') {
      result = result.filter(i => i.purchaseType === 'Bundle')
    }
    if (quickFilter && QUICK_FILTER_MAP[quickFilter]) {
      const [typeTerm, subTerm] = QUICK_FILTER_MAP[quickFilter]
      result = result.filter(i => {
        const t = (i.itemType || '').toLowerCase()
        const s1 = (i.subType || '').toLowerCase()
        const s2 = (i.subType2 || '').toLowerCase()
        return t.includes(typeTerm) && (s1.includes(subTerm) || s2.includes(subTerm))
      })
    }
    if (advPurchaseType) {
      result = result.filter(i => (i.purchaseType || '').toLowerCase() === advPurchaseType.toLowerCase())
    }
    if (advAccessType) {
      result = result.filter(i => (i.accessType || '').toLowerCase() === advAccessType.toLowerCase())
    }
    const term = searchDebounce.toLowerCase().trim()
    if (!term) return result
    return result.filter(i =>
      (i.productName || '').toLowerCase().includes(term) ||
      (i.itemType || '').toLowerCase().includes(term) ||
      (i.purchaseType || '').toLowerCase().includes(term)
    )
  }, [items, searchDebounce, quickFilter, advPurchaseType, advAccessType, QUICK_FILTER_MAP])

  const bundleGroups = useMemo(() => {
    const groups = {}
    filtered.forEach(i => {
      if (i.purchaseType === 'Bundle' && i.bundleName) {
        if (!groups[i.bundleName]) groups[i.bundleName] = []
        groups[i.bundleName].push(i)
      }
    })
    return Object.entries(groups)
  }, [filtered])

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    return filtered.slice(startIndex, startIndex + itemsPerPage)
  }, [filtered, currentPage, itemsPerPage])

  const totalPages = Math.ceil(filtered.length / itemsPerPage)

  const activeFilters = useMemo(() => {
    const filters = []
    if (searchDebounce) filters.push({ type: 'search', value: searchDebounce, label: `Search: "${searchDebounce}"` })
    if (quickFilter) {
      const [typeTerm, subTerm] = QUICK_FILTER_MAP[quickFilter]
      filters.push({ type: 'quick', value: quickFilter, label: `${typeTerm} - ${subTerm}` })
    }
    if (advPurchaseType) filters.push({ type: 'purchase', value: advPurchaseType, label: `Purchase: ${advPurchaseType}` })
    if (advAccessType) filters.push({ type: 'access', value: advAccessType, label: `Access: ${prettify(advAccessType)}` })
    return filters
  }, [searchDebounce, quickFilter, advPurchaseType, advAccessType, QUICK_FILTER_MAP, prettify])

  const clearFilter = useCallback((filterType) => {
    switch (filterType) {
      case 'search':
        setSearch('')
        break
      case 'quick':
        setQuickFilter('')
        break
      case 'purchase':
        setAdvPurchaseType('')
        break
      case 'access':
        setAdvAccessType('')
        break
      default:
        break
    }
  }, [])


  const openFileDialog = () => {
    if (fileInputRef.current) fileInputRef.current.click()
  }

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      setMatchLoading(true)
      // 1. presign
      const presignRes = await fetch(`${API_BASE}/${API_VERSION}/private/gym-class-item-category/search/upload`, { method: 'POST' })
      if (!presignRes.ok) throw new Error('Failed to presign URL')
      const { url, key } = await presignRes.json()

      // 2. PUT upload
      await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } })

      // 3. poll for result
      let result
      for (let i = 0; i < 30; i++) {
        const res = await fetch(`${API_BASE}/${API_VERSION}/private/gym-class-item-category/search/match/${key}`)
        if (res.status === 200) {
          result = await res.json()
          break
        }
        await new Promise(r => setTimeout(r, 1000))
      }
      if (!result) throw new Error('Timed out fetching match')

      const ids = result.map(r => r.item_id)
      const matched = items.filter(i => ids.includes(i.item_id))
      setMatchedItems(matched)
      setMatchModalOpen(true)
    } catch (err) {
      notify('error', err.message)
    } finally {
      setMatchLoading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  return (
    <div className="items-page">
      {loading && <GlobalThrobberComponent />}
      <div className="search-bar">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search items" className="item-search" />
        <div className="quick-filters">
          {QUICK_BUTTONS.map(([key, icon]) => (
            <button
              key={key}
              className={`filter-button ${quickFilter === key ? 'active' : ''}`}
              onClick={() => setQuickFilter(quickFilter === key ? '' : key)}
              title={key === 'bundles_only' ? 'Bundles' : (QUICK_FILTER_MAP[key] ? `${QUICK_FILTER_MAP[key][0]} - ${QUICK_FILTER_MAP[key][1]}` : '')}
            >
              {icon}
            </button>
          ))}
        </div>
        <button className="filter-toggle" onClick={() => setShowAdvanced(!showAdvanced)}>⚙️</button>
        {activeFilters.length > 0 && (
          <button
            className="clear-all-filters"
            onClick={() => { setSearch(''); setQuickFilter(''); setAdvPurchaseType(''); setAdvAccessType('') }}
          >
            Clear All
          </button>
        )}
        <button className="screenshot-match-button" onClick={openFileDialog}>
          {matchLoading ? 'Matching…' : 'Screenshot Match'}
        </button>
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>
      <div className={`advanced-filters ${showAdvanced ? 'open' : ''}`}>
        <select value={advPurchaseType} onChange={e => setAdvPurchaseType(e.target.value)}>
          {PURCHASE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt || 'Any Purchase Type'}</option>)}
        </select>
        <select value={advAccessType} onChange={e => setAdvAccessType(e.target.value)}>
          {ACCESS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt ? prettify(opt) : 'Any Access Type'}</option>)}
        </select>
      </div>
      {activeFilters.length > 0 && (
        <div className="active-filters">
          <span className="active-filters-label">Active filters:</span>
          <div className="filter-chips">
            {activeFilters.map((filter, index) => (
              <div key={index} className="filter-chip">
                <span className="filter-chip-label">{filter.label}</span>
                <button
                  className="filter-chip-remove"
                  onClick={() => clearFilter(filter.type)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {bundleGroups.length > 0 && (
        <div className="bundles-rail">
          {bundleGroups.map(([name, items]) => (
            <div key={name} className="bundle-card">
              <div
                className="bundle-hero"
                onClick={() => setSelectedBundle({ name, items })}
              >
                <img src={items[0].imageUrl} alt={name} />
                <div className="bundle-info">
                  <div className="bundle-name">{name}</div>
                  <div className="bundle-count">{items.length} items · {items[0].bundlePrice} coins</div>
                </div>
              </div>
              {expandedBundles[name] && (
                <div className="bundle-items-grid">
                  {items.map(item => (
                    <div key={item.item_id} className="bundle-item-card">
                      <CarouselComponent mediaItems={[{ type: 'image', url: item.imageUrl }]} productName={item.productName} />
                      <div className="bundle-item-name">{item.productName}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="items-grid">
        {paginatedItems.map(item => (
          <div key={item.id} className="item-card">
            <div className="item-media">
              {(() => {
                const media = []
                if (item.imageUrl) media.push({ type: 'image', url: item.imageUrl })
                if (item.videoUrl) media.push({ type: 'video', url: item.videoUrl })
                return <CarouselComponent mediaItems={media} productName={item.productName} />
              })()}
            </div>
            <div className="item-details">
              <h4 className="item-name">{item.productName}</h4>
              <div className="item-info">
                <div className="item-info-group">
                  <div className="item-info-label">Type</div>
                  <span className={`item-type ${(item.itemType || '').toLowerCase()}`}>{item.itemType}{item.subType && <span className="sub-type"> - {item.subType}</span>}{item.subType2 && <span className="sub-type2"> - {item.subType2}</span>}</span>
                </div>
                <div className="item-info-group">
                  <div className="item-info-label">Purchase</div>
                  <span className={`purchase-type ${(item.purchaseType || '').toLowerCase()}`}>{item.purchaseType}</span>
                </div>
              </div>
              <div className="item-details-row">
                <div className="price-section">
                  <div className="item-info-label">Price</div>
                  <span className="item-price">{item.purchaseType === 'Bundle' ? (item.bundlePriceType === 'Both' ? `${item.bundleCoinPrice} coins / $${item.bundleDollarPrice}` : item.bundlePrice) : item.price ? `${item.price} ${item.purchaseType === 'Purchase' ? 'USD' : 'coins'}` : 'Free'}</span>
                </div>
                <div className="access-section">
                  <div className="item-info-label">Access</div>
                  <span className={`access-type ${(item.accessType || '').toLowerCase()}`}>{prettify(item.accessType)}</span>
                </div>
              </div>
              {item.seasonPass !== 'None' && <div className="item-season-pass"><div className="season-pass-label">Season Pass</div><div>{item.seasonPass} - {item.seasonPassName}</div></div>}
              <div className="item-status">
                <div className="status-pair">
                  <div className="status-label">Availability</div>
                  <span className={`availability ${item.isUnlockable ? 'available' : 'unavailable'}`}>{item.isUnlockable ? 'Available' : 'Unavailable'}</span>
                </div>
                <div className="status-pair">
                  <div className="status-label">Released</div>
                  <span className="release-date">{formatDate(item.released)}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="pagination-controls">
          <button
            className="page-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          >
            ‹
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
            // show first, last, and +/-2 around current
            if (
              page === 1 ||
              page === totalPages ||
              Math.abs(page - currentPage) <= 2
            ) {
              return (
                <button
                  key={page}
                  className={`page-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              )
            }
            // insert ellipsis once between gaps
            if (
              (page === 2 && currentPage > 4) ||
              (page === totalPages - 1 && currentPage < totalPages - 3)
            ) {
              return <span key={page} className="page-ellipsis">…</span>
            }
            return null
          })}
          <button
            className="page-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          >
            ›
          </button>
        </div>
      )}
      {selectedBundle && (
        <div className="bundle-modal">
          <div className="bundle-modal-backdrop" onClick={() => setSelectedBundle(null)} />
          <div className="bundle-modal-content">
            <button className="bundle-modal-close" onClick={() => setSelectedBundle(null)}>✕</button>
            <h3 className="bundle-modal-title">{selectedBundle.name}</h3>
            <div className="bundle-modal-grid">
              {selectedBundle.items.map(item => (
                <div key={item.item_id} className="bundle-modal-item">
                  <CarouselComponent mediaItems={[{ type: 'image', url: item.imageUrl }]} productName={item.productName} />
                  <div className="bundle-modal-item-info">
                    <div className="bundle-modal-item-name">{item.productName}</div>
                    <div className="bundle-modal-item-type">{item.itemType} {item.subType && `- ${item.subType}`}{item.subType2 && ` - ${item.subType2}`}</div>
                    <div className="bundle-modal-item-price">{item.bundlePrice ? `${item.bundlePrice} coins` : 'Included'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {matchModalOpen && (
        <div className="bundle-modal">
          <div className="bundle-modal-backdrop" onClick={() => setMatchModalOpen(false)} />
          <div className="bundle-modal-content">
            <button className="bundle-modal-close" onClick={() => setMatchModalOpen(false)}>✕</button>
            <h3 className="bundle-modal-title">Visual Matches</h3>
            <div className="bundle-modal-grid">
              {matchedItems.map(item => (
                <div key={item.item_id} className="bundle-modal-item">
                  <CarouselComponent mediaItems={[{ type: 'image', url: item.imageUrl }]} productName={item.productName} />
                  <div className="bundle-modal-item-info">
                    <div className="bundle-modal-item-name">{item.productName}</div>
                    <div className="bundle-modal-item-type">{item.itemType} {item.subType && `- ${item.subType}`}{item.subType2 && ` - ${item.subType2}`}</div>
                  </div>
                </div>
              ))}
              {matchedItems.length === 0 && <div>No matches found.</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
